import { arrayMove } from '@dnd-kit/sortable';
import { Box, Button, CircularProgress, useTheme } from '@material-ui/core';
import {
  Backdrop,
  Checkbox,
  PlusIconTwoTone,
  Typography,
  WarningCircleFilledIcon,
} from '@room-match/shared-ui-components';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Link } from 'react-router-dom';
import { COLORS } from 'shared/constants/COLORS';
import { RESUME_DEFAULT_DATA } from 'shared/constants/INITIAL_RESUME';
import { IResumeCredit } from 'shared/interfaces/IResume';
import { ICategory, ICredit, IResume } from 'shared/interfaces/ITalent';
import { talentService } from 'shared/services/talentService';
import { arraysEqual } from 'shared/utils/common';
import { v4 as uuidv4 } from 'uuid';
import { captureMessage } from '../../../lib/sentry';
import CategoryModal from './CategoryModal/CategoryModal';
import { AddResumeButton, Bold, CheckboxContainer, Header, HeaderRight, MainCont, Title } from './Resume.styles';
import ResumeCategoryItem from './ResumeCategoryItem/ResumeCategoryItem';

const { getResume, updateTalent } = talentService();

const Resume = () => {
  const theme = useTheme();
  const { mutateAsync: updateTalentResume } = updateTalent();
  const { data: talentResumeData, isLoading, refetch } = getResume();
  const [resume, setresume] = useState<ICategory[]>([]);
  const [hideYears, setHideYears] = useState<boolean>(false);
  const [savingYears, setSavingYears] = useState<boolean>(false);
  const [showAdd, setshowAdd] = useState(false);
  const [failedToLoad, setfailedToLoad] = useState(false);
  const [isInitializing, setisInitializing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!talentResumeData) return;

    const resumesData: IResume['attributes']['resume']['categories'] =
      talentResumeData?.data.attributes.resume?.categories || [];
    const hideYears: IResume['attributes']['resume']['hide_years'] =
      talentResumeData?.data.attributes.resume?.hide_years || false;

    setHideYears(hideYears);
    setresume([...resumesData]);

    //removed to avoid overwriting existing data
    // if (resumesData.length === 0 && !isLoading) {
    //   updateTalentResume(
    //     {
    //       resume: DEFAULT_DATA,
    //     },
    //     {
    //       onSuccess: () => {
    //         setresume(DEFAULT_DATA.categories);
    //       },
    //     },
    //   );
    // }
    if (resumesData.length === 0 && !isLoading) {
      setfailedToLoad(true);
      captureMessage('user failed to load resume data');
    }
  }, [talentResumeData]);

  const resumeUpdater = async (data: ICategory[]) => {
    await updateTalentResume(
      {
        resume: {
          categories: data,
          hide_years: hideYears,
        },
      },
      {
        onSuccess: () => {
          // captureMessage('user updated resume');
          setresume([...data]);
          refetch();
        },
      },
    );
    setDeleting(false);
  };

  const resumeCreditUpdater = (data: IResumeCredit, categoryID: string) => {
    const updatedData = resume.map((m) => {
      if (m.category_id === categoryID) {
        return {
          ...m,
          credits: [{ ...data }, ...m.credits],
        };
      }
      return m;
    });

    // captureMessage({
    //   message: 'user added resume credit',
    //   data: updatedData,
    // });
    resumeUpdater(updatedData);
  };

  const onDragEnd = (result: any) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const swappedArray = arrayMove(resume, result.source.index, result.destination.index);

    if (arraysEqual(swappedArray, resume)) return;
    setresume(swappedArray);
    resumeUpdater(swappedArray);
  };

  const getListStyle = (isDraggingOver: boolean) => ({
    background:
      theme.palette.type === 'dark' ? (isDraggingOver ? '#151a21' : '#0b0f14') : isDraggingOver ? '#f2f2f2' : '#fff',
    felx: 1,
    transition: '300ms',
  });

  const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
    borderRadius: 8,
    display: 'flex',
    userSelect: 'none',
    border: isDragging ? '2px solid #2d343e' : 'none',
    margin: '20px 0',
    ...draggableStyle,
  });

  const modifyCredits = (data: ICredit[], categoryID: string) => {
    const updatedData = resume.map((m) => {
      if (m.category_id === categoryID) {
        return {
          ...m,
          credits: data,
        };
      }
      return m;
    });

    resumeUpdater(updatedData);
  };

  const handleChange = () => {
    setHideYears(!hideYears);
    setSavingYears(true);
    updateTalentResume(
      {
        resume: {
          categories: resume,
          hide_years: !hideYears,
        },
      },
      {
        onSuccess: (datasss) => {
          setSavingYears(false);
        },
      },
    );
  };

  const handleSelect = async (e: string) => {
    const newData = {
      category: e,
      category_id: uuidv4(),
      credits: [],
    };
    setresume([newData, ...resume]);
    await resumeUpdater([newData, ...resume]);
    setshowAdd(false);
  };

  const handleDeleteCategory = (id: string) => {
    setDeleting(true);
    const modifiedData = resume.filter((f) => f.category_id !== id);
    setresume(modifiedData);
    resumeUpdater(modifiedData);
  };

  const initializeResume = () => {
    setisInitializing(true);
    updateTalentResume(
      {
        resume: RESUME_DEFAULT_DATA,
      },
      {
        onSuccess: () => {
          setisInitializing(false);
          setresume(RESUME_DEFAULT_DATA.categories);
        },
      },
    );
  };

  return resume.length === 0 ? (
    isLoading && !failedToLoad ? (
      <Backdrop isLoading={isLoading} />
    ) : (
      <div
        style={{
          minHeight: 140,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 40,
          padding: 40,
        }}
      >
        <WarningCircleFilledIcon style={{ width: 50, height: 50 }} />
        <p style={{ maxWidth: 500, textAlign: 'center', fontWeight: 700, fontSize: 20 }}>
          Your Resume Data failed to load
        </p>

        <p style={{ maxWidth: 650, textAlign: 'center', fontSize: 18 }}>
          Please hold down the SHIFT key on your computer and select reload in your browser, or reload the page if you
          are on mobile.{' '}
          <Link
            to={'/talent/my_info/resume'}
            onClick={() => window.location.reload()}
            style={{ textDecoration: 'none' }}
          >
            <Bold> Reload the Page</Bold>
          </Link>
        </p>

        <p style={{ maxWidth: 650, textAlign: 'center', fontSize: 18 }}>
          If this is your <Bold>first visit to the Resume Page</Bold>, and you haven't added any credits yet, you can
          click the button below to create a blank resume.
        </p>
        <Typography fontSize={18} style={{ maxWidth: 650, textAlign: 'center', marginBottom: -40 }}>
          <Bold style={{ color: COLORS.ERROR }}>IMPORTANT: </Bold>
        </Typography>
        <Typography fontSize={18} style={{ maxWidth: 650, textAlign: 'center', color: COLORS.ERROR }}>
          Do not click this button if you have previously added credits to your resume as it will erase them.
        </Typography>

        <Button disabled={isInitializing} onClick={initializeResume} variant="contained" color="primary" size="large">
          {isInitializing ? <CircularProgress size={18} /> : 'CREATE A NEW BLANK RESUME'}
        </Button>
      </div>
    )
  ) : (
    <MainCont>
      <Header>
        <Title>Resume</Title>
        <HeaderRight>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
            {savingYears ? (
              <CircularProgress style={{ width: 20, height: 20, marginRight: 10 }} />
            ) : (
              <CheckboxContainer>
                <Checkbox onChange={handleChange} checked={hideYears} value={hideYears} label="Hide&nbsp;Years" />
              </CheckboxContainer>
            )}
          </div>
          <AddResumeButton onClick={() => setshowAdd(true)} fullWidth disabled={resume.length >= 12}>
            <PlusIconTwoTone style={{ marginRight: 5 }} />
            <p style={{ fontSize: 14 }}>Add Resume Category</p>
          </AddResumeButton>
        </HeaderRight>
      </Header>
      <Box style={{ minHeight: '73vh', padding: '0 20px' }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div {...provided.droppableProps} ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
                {resume?.length ? (
                  resume.map((item: ICategory, index: number) => (
                    <Draggable
                      key={item.category_id}
                      draggableId={item.category_id || `${item.category + index + item.category_id}`}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                        >
                          <ResumeCategoryItem
                            deleting={deleting}
                            ifLastItem={resume.length > 1 ? false : true}
                            deleteCategory={() => handleDeleteCategory(item.category_id)}
                            hideYears={hideYears}
                            modifyCredits={(data: ICredit[]) => modifyCredits(data, item.category_id)}
                            addCredit={(data: IResumeCredit) => resumeCreditUpdater(data, item.category_id)}
                            item={item}
                            key={item.category_id}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))
                ) : (
                  <></>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
      {showAdd && (
        <CategoryModal
          show={showAdd}
          close={() => setshowAdd(false)}
          onselect={handleSelect}
          added={resume.map((m) => m.category) as string[]}
        />
      )}
    </MainCont>
  );
};

export default Resume;
